import { useEffect, useRef, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import Toast from '@/utils/toastManager';
import '../PlanPage/CustomCheckoutModal.css';
import './MyAccountPaymentMethodModal.css';

const COUNTRIES = [
	{ code: 'US', name: 'United States' },
	{ code: 'CA', name: 'Canada' },
	{ code: 'GB', name: 'United Kingdom' },
	{ code: 'AU', name: 'Australia' },
	{ code: 'NZ', name: 'New Zealand' },
	{ code: 'IN', name: 'India' },
	{ code: 'AF', name: 'Afghanistan' },
	{ code: 'AL', name: 'Albania' },
	{ code: 'DZ', name: 'Algeria' },
	{ code: 'AR', name: 'Argentina' },
	{ code: 'AT', name: 'Austria' },
	{ code: 'BE', name: 'Belgium' },
	{ code: 'BR', name: 'Brazil' },
	{ code: 'CN', name: 'China' },
	{ code: 'DK', name: 'Denmark' },
	{ code: 'EG', name: 'Egypt' },
	{ code: 'FI', name: 'Finland' },
	{ code: 'FR', name: 'France' },
	{ code: 'DE', name: 'Germany' },
	{ code: 'GR', name: 'Greece' },
	{ code: 'HK', name: 'Hong Kong' },
	{ code: 'IE', name: 'Ireland' },
	{ code: 'IL', name: 'Israel' },
	{ code: 'IT', name: 'Italy' },
	{ code: 'JP', name: 'Japan' },
	{ code: 'MX', name: 'Mexico' },
	{ code: 'NL', name: 'Netherlands' },
	{ code: 'NO', name: 'Norway' },
	{ code: 'PL', name: 'Poland' },
	{ code: 'PT', name: 'Portugal' },
	{ code: 'RU', name: 'Russia' },
	{ code: 'SA', name: 'Saudi Arabia' },
	{ code: 'SG', name: 'Singapore' },
	{ code: 'ZA', name: 'South Africa' },
	{ code: 'KR', name: 'South Korea' },
	{ code: 'ES', name: 'Spain' },
	{ code: 'SE', name: 'Sweden' },
	{ code: 'CH', name: 'Switzerland' },
	{ code: 'TH', name: 'Thailand' },
	{ code: 'TR', name: 'Turkey' },
	{ code: 'AE', name: 'United Arab Emirates' }
];

const formatMinorUnitAmount = (amount = 0, currencyCode = 'USD') => {
	try {
		return new Intl.NumberFormat(undefined, {
			style: 'currency',
			currency: currencyCode
		}).format((Number(amount) || 0) / 100);
	} catch {
		return `$${((Number(amount) || 0) / 100).toFixed(2)}`;
	}
};

const formatUnixDate = value => {
	if (!value) return 'Not available';
	return new Date(value * 1000).toLocaleDateString(undefined, {
		month: 'short',
		day: 'numeric',
		year: 'numeric'
	});
};

const formatInvoiceStatus = value =>
	`${value || 'unknown'}`
		.replace(/[_-]+/g, ' ')
		.replace(/\b\w/g, char => char.toUpperCase());

const formatPaymentMethodType = value =>
	`${value || 'payment_method'}`
		.replace(/[_-]+/g, ' ')
		.replace(/\b\w/g, char => char.toUpperCase());

const getNormalizedPaymentMethodType = method => {
	const rawType = `${method?.normalizedType || method?.type || ''}`.toLowerCase();
	const gateway = `${method?.gateway || ''}`.toLowerCase();

	if (rawType.includes('paypal') || gateway.includes('paypal')) return 'paypal';
	if (rawType.includes('apple') || gateway.includes('apple')) return 'apple_pay';
	if (rawType === 'card' || rawType === 'ccard' || gateway.includes('stripe') || gateway.includes('card')) return 'card';

	return rawType || gateway || 'payment_method';
};

const getSavedPaymentMethodLabel = method => {
	if (method?.displayLabel) return method.displayLabel;

	const normalizedType = getNormalizedPaymentMethodType(method);
	const gatewayLabel = method?.gateway ? ` via ${method.gateway}` : '';

	if (normalizedType === 'paypal') {
		const maskedEmail =
			method?.paypal?.email ||
			method?.email ||
			method?.masked_email ||
			method?.customer_email;

		return maskedEmail ? `PayPal saved (${maskedEmail})` : `PayPal saved${gatewayLabel}`;
	}

	if (normalizedType === 'apple_pay') {
		const cardBrand = method?.card?.brand || method?.brand;
		const last4 = method?.card?.last4 || method?.last4;
		return cardBrand && last4
			? `Apple Pay saved (${cardBrand.toUpperCase()} ending in ${last4})`
			: `Apple Pay saved${gatewayLabel}`;
	}

	if (normalizedType === 'card') {
		const cardBrand = method?.card?.brand || method?.brand || 'Card';
		const last4 = method?.card?.last4 || method?.last4 || method?.masked_card_number?.slice(-4);
		const expMonth = method?.card?.exp_month || method?.exp_month;
		const expYear = method?.card?.exp_year || method?.exp_year;

		if (last4) {
			const expiry = expMonth && expYear ? ` (${String(expMonth).padStart(2, '0')}/${expYear})` : '';
			return `${cardBrand.toUpperCase()} ending in ${last4}${expiry}`;
		}

		return `${cardBrand.toUpperCase()} saved${gatewayLabel}`;
	}

	return `${normalizedType.replace(/_/g, ' ')} saved${gatewayLabel}`;
};

const getSavedPaymentMethodDetails = method => {
	if (method?.maskedDetails) return method.maskedDetails;

	const normalizedType = getNormalizedPaymentMethodType(method);

	if (normalizedType === 'paypal') {
		return method?.paypal?.email || method?.email || method?.masked_email || 'Saved PayPal account on file';
	}

	if (normalizedType === 'apple_pay' || normalizedType === 'card') {
		const last4 = method?.card?.last4 || method?.last4 || method?.masked_card_number?.slice(-4);
		return last4 ? `Ending in ${last4}` : 'Saved payment method on file';
	}

	return 'Saved payment method on file';
};

const normalizePaymentMethod = method => ({
	...method,
	normalizedType: getNormalizedPaymentMethodType(method),
	displayLabel: getSavedPaymentMethodLabel(method),
	maskedDetails: getSavedPaymentMethodDetails(method),
	isPrimary: Boolean(method?.isPrimary),
	expMonth: method?.expMonth || method?.card?.exp_month || method?.exp_month || null,
	expYear: method?.expYear || method?.card?.exp_year || method?.exp_year || null
});

function MyAccountPaymentMethodModal({ open, onClose, onPaymentMethodUpdated, subscriptionDetails }) {
	const [loading, setLoading] = useState(false);
	const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
	const [currentPaymentMethods, setCurrentPaymentMethods] = useState([]);
	const [selectedNewPaymentType, setSelectedNewPaymentType] = useState('card');
	const [invoicesExpanded, setInvoicesExpanded] = useState(false);
	const [paypalLoading, setPaypalLoading] = useState(false);
	const [applePayAvailable, setApplePayAvailable] = useState(false);
	const paypalInitialized = useRef(false);
	const applePayMounted = useRef(false);
	const applePayContainerRef = useRef(null);
	const [paymentData, setPaymentData] = useState({
		cardholderName: '',
		cardNumber: '',
		cardExpiry: '',
		cardCvv: '',
		zipCode: '',
		country: 'US'
	});
	const [errors, setErrors] = useState({});
	const [primaryPrompt, setPrimaryPrompt] = useState(null);
	const [settingPrimary, setSettingPrimary] = useState(false);
	const [settingPrimaryId, setSettingPrimaryId] = useState(null);

	const loadCurrentPaymentMethods = async signalActive => {
		setPaymentMethodsLoading(true);

		try {
			const userData = JSON.parse(localStorage.getItem('userData') || '{}');
			if (!userData?.email) {
				if (signalActive?.()) setCurrentPaymentMethods([]);
				return;
			}

			const response = await axiosConfig.get(`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-methods`, {
				params: { email: userData.email }
			});

			if (!signalActive?.()) return;

			const paymentMethods = Array.isArray(response.data?.paymentMethods) ? response.data.paymentMethods : [];
			setCurrentPaymentMethods(paymentMethods.map(normalizePaymentMethod));
		} catch (error) {
			if (signalActive?.()) {
				setCurrentPaymentMethods([]);
				Toast.error(error.response?.data?.error || error.message || 'Failed to load current payment methods');
			}
		} finally {
			if (signalActive?.()) {
				setPaymentMethodsLoading(false);
			}
		}
	};

	useEffect(() => {
		if (!open) {
			setLoading(false);
			setPaymentMethodsLoading(false);
			setCurrentPaymentMethods([]);
			setSelectedNewPaymentType('card');
			setPaypalLoading(false);
			setApplePayAvailable(false);
			setPaymentData({
				cardholderName: '',
				cardNumber: '',
				cardExpiry: '',
				cardCvv: '',
				zipCode: '',
				country: 'US'
			});
			setErrors({});
			setSettingPrimaryId(null);
		}
	}, [open]);

	useEffect(() => {
		if (!open) return;

		let isActive = true;
		loadCurrentPaymentMethods(() => isActive);

		return () => {
			isActive = false;
		};
	}, [open]);

	const handleInputChange = (field, value) => {
		let nextValue = value;

		if (field === 'cardNumber') {
			nextValue = value
				.replace(/\D/g, '')
				.slice(0, 16)
				.replace(/(.{4})/g, '$1 ')
				.trim();
		}

		if (field === 'cardExpiry') {
			const rawValue = value.replace(/\D/g, '').slice(0, 4);
			nextValue = rawValue.length > 2 ? `${rawValue.slice(0, 2)}/${rawValue.slice(2)}` : rawValue;
		}

		if (field === 'cardCvv') {
			nextValue = value.replace(/\D/g, '').slice(0, 4);
		}

		if (field === 'zipCode') {
			const detectedCountry = detectCountryFromZip(nextValue);
			setPaymentData(prev => ({ ...prev, zipCode: nextValue, country: detectedCountry }));
			setErrors(prev => ({ ...prev, zipCode: '', country: '' }));
			return;
		}

		setPaymentData(prev => ({ ...prev, [field]: nextValue }));
		setErrors(prev => ({ ...prev, [field]: '' }));
	};

	const detectCountryFromZip = zip => {
		const cleanedZip = `${zip || ''}`.trim().toUpperCase();
		if (/^[A-Z]\d[A-Z] ?\d[A-Z]\d$/.test(cleanedZip)) return 'CA';
		if (/^[A-Z]{1,2}\d{1,2}[A-Z]? ?\d[A-Z]{2}$/.test(cleanedZip)) return 'GB';
		if (/^\d{6}$/.test(cleanedZip)) return 'IN';
		if (/^\d{4}$/.test(cleanedZip)) return 'AU';
		if (/^\d{5}(-?\d{4})?$/.test(cleanedZip)) return 'US';

		return 'US';
	};

	const validateZipCode = zip => {
		if (!zip || !zip.trim()) return false;
		const country = detectCountryFromZip(zip);
		const patterns = {
			US: /^\d{5}(-\d{4})?$/,
			AU: /^\d{4}$/,
			CA: /^[A-Z]\d[A-Z] ?\d[A-Z]\d$/i,
			GB: /^[A-Z]{1,2}\d{1,2}[A-Z]? ?\d[A-Z]{2}$/i,
			NZ: /^\d{4}$/,
			IN: /^\d{6}$/
		};

		if (!patterns[country]) {
			return zip.length >= 3 && zip.length <= 10;
		}

		return patterns[country].test(zip.trim());
	};

	const validatePaymentData = () => {
		const newErrors = {};

		if (!paymentData.cardholderName.trim()) newErrors.cardholderName = 'Cardholder name is required';

		const cardNum = paymentData.cardNumber.replace(/\s/g, '');
		if (!cardNum || cardNum.length < 13 || cardNum.length > 19) newErrors.cardNumber = 'Invalid card number';

		if (!paymentData.cardExpiry || paymentData.cardExpiry.length !== 5) {
			newErrors.cardExpiry = 'Invalid expiry date (MM/YY)';
		}

		if (!paymentData.cardCvv || paymentData.cardCvv.length < 3) newErrors.cardCvv = 'Invalid CVV';

		if (!paymentData.zipCode.trim()) {
			newErrors.zipCode = 'Zip/Postal code is required';
		} else if (!validateZipCode(paymentData.zipCode)) {
			newErrors.zipCode = 'Invalid zip/postal code format';
		}

		if (!paymentData.country) newErrors.country = 'Country is required';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const getChargebeeInstance = async () => {
		if (!window.Chargebee) {
			const existingScript = document.querySelector('script[data-chargebee-sdk="true"]');

			if (!existingScript) {
				const script = document.createElement('script');
				script.src = 'https://js.chargebee.com/v2/chargebee.js';
				script.async = true;
				script.dataset.chargebeeSdk = 'true';
				document.body.appendChild(script);

				await new Promise((resolve, reject) => {
					script.onload = resolve;
					script.onerror = reject;
				});
			} else if (!window.Chargebee) {
				await new Promise(resolve => {
					existingScript.addEventListener('load', resolve, { once: true });
				});
			}
		}

		return window.Chargebee.init({
			site: import.meta.env.VITE_CHARGEBEE_SITE,
			publishableKey: import.meta.env.VITE_APP_CHARGEBEE_API_KEY
		});
	};

	const handleSuccess = async (message, newPaymentSourceId) => {
		Toast.success(message);
		await loadCurrentPaymentMethods(() => true);
		if (typeof onPaymentMethodUpdated === 'function') {
			await onPaymentMethodUpdated();
		}
		setPaymentData({
			cardholderName: '',
			cardNumber: '',
			cardExpiry: '',
			cardCvv: '',
			zipCode: '',
			country: 'US'
		});
		setErrors({});
		if (newPaymentSourceId) {
			setPrimaryPrompt({ paymentSourceId: newPaymentSourceId });
		}
	};

	const setPrimaryPaymentMethod = async paymentSourceId => {
		if (!paymentSourceId) return;
		const userData = JSON.parse(localStorage.getItem('userData') || '{}');
		setSettingPrimary(true);
		setSettingPrimaryId(paymentSourceId);
		try {
			await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-sources/set-primary`, {
				email: userData.email,
				paymentSourceId
			});
			Toast.success('Primary payment method updated!');
			await loadCurrentPaymentMethods(() => true);
			if (typeof onPaymentMethodUpdated === 'function') {
				await onPaymentMethodUpdated();
			}
		} catch (error) {
			Toast.error(error.response?.data?.error || error.message || 'Failed to set primary payment method');
		} finally {
			setSettingPrimary(false);
			setSettingPrimaryId(null);
		}
	};

	const handleSetPrimary = async () => {
		if (!primaryPrompt?.paymentSourceId) return;
		await setPrimaryPaymentMethod(primaryPrompt.paymentSourceId);
		setPrimaryPrompt(null);
	};

	const handleSkipPrimary = () => {
		setPrimaryPrompt(null);
	};

	const handleCardPaymentSourceUpdate = async () => {
		if (!validatePaymentData()) {
			Toast.error('Please fill all payment fields correctly');
			return;
		}

		setLoading(true);

		try {
			const userData = JSON.parse(localStorage.getItem('userData') || '{}');
			const [expMonth, expYear] = paymentData.cardExpiry.split('/');

			const cardResponse = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-sources/card`, {
				email: userData.email,
				cardNumber: paymentData.cardNumber.replace(/\s/g, ''),
				cardExpMonth: expMonth,
				cardExpYear: expYear,
				cardCvv: paymentData.cardCvv,
				cardholderName: paymentData.cardholderName,
				billingZip: paymentData.zipCode,
				billingCountry: paymentData.country
			});

			setLoading(false);
			await handleSuccess('Payment method updated successfully!', cardResponse.data?.paymentSource?.id);
		} catch (error) {
			setLoading(false);
			Toast.error(error.response?.data?.error || error.message || 'Failed to update card payment source');
		}
	};

	useEffect(() => {
		if (!open || selectedNewPaymentType !== 'paypal') return;
		if (paypalInitialized.current) return;

		let isActive = true;

		const initPaypal = async () => {
			setPaypalLoading(true);

			try {
				const userData = JSON.parse(localStorage.getItem('userData') || '{}');
				const cb = await getChargebeeInstance();
				const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/paypal-payment-intent`, {
					amount: 100,
					currency_code: 'USD',
					email: userData.email,
					firstName: userData.firstname || userData.firstName || '',
					lastName: userData.lastname || userData.lastName || ''
				});

				if (!isActive) return;

				const paymentIntent = response.data?.payment_intent;
				const paypalHandler = await cb.load('paypal');
				paypalHandler.setPaymentIntent(paymentIntent);

				const container = document.getElementById('account-paypal-container');
				if (container) container.innerHTML = '';

				await paypalHandler.mountPaymentButton('#account-paypal-container', {
					style: {
						layout: 'vertical',
						color: 'white',
						shape: 'rect',
						label: 'paypal',
						size: 'responsive'
					}
				});

				if (!isActive) return;

				paypalInitialized.current = true;
				setPaypalLoading(false);

				paypalHandler
					.handlePayment()
					.then(async authorizedIntent => {
						if (!authorizedIntent || authorizedIntent.status !== 'authorized') {
							if (isActive) Toast.error('PayPal authorization was not completed.');
							return;
						}

						setLoading(true);

						try {
							const paypalResponse = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-sources/payment-intent`, {
								email: userData.email,
								paymentIntentId: authorizedIntent.id,
								paymentMethodType: 'paypal_express_checkout'
							});

							setLoading(false);
							await handleSuccess('Payment method updated successfully!', paypalResponse.data?.paymentSource?.id);
						} catch (error) {
							setLoading(false);
							Toast.error(error.response?.data?.error || error.message || 'Failed to update PayPal payment source');
						}
					})
					.catch(error => {
						if (isActive) {
							setPaypalLoading(false);
							Toast.error(error.message || 'Unable to initialize PayPal');
						}
					});
			} catch (error) {
				if (isActive) {
					setPaypalLoading(false);
					Toast.error(error.response?.data?.error || error.message || 'Unable to initialize PayPal');
				}
			}
		};

		initPaypal();

		return () => {
			isActive = false;
			paypalInitialized.current = false;
			const container = document.getElementById('account-paypal-container');
			if (container) container.innerHTML = '';
		};
	}, [open, selectedNewPaymentType]);

	useEffect(() => {
		if (!open || selectedNewPaymentType !== 'apple_pay') return;
		if (applePayMounted.current) return;

		let isActive = true;

		const initApplePay = async () => {
			try {
				const userData = JSON.parse(localStorage.getItem('userData') || '{}');
				const cb = await getChargebeeInstance();
				const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/create-payment-intent`, {
					amount: 100,
					currency_code: 'USD',
					payment_method_type: 'apple_pay',
					email: userData.email,
					firstName: userData.firstname || userData.firstName || '',
					lastName: userData.lastname || userData.lastName || ''
				});

				if (!isActive) return;

				const paymentIntent = response.data?.payment_intent;
				const applePayHandler = await cb.load('apple-pay');
				applePayHandler.setPaymentIntent(paymentIntent);

				let available = true;
				if (typeof applePayHandler.canMakePayments === 'function') {
					const capability = await applePayHandler.canMakePayments();
					available = capability !== false;
				}

				if (!available) {
					setApplePayAvailable(false);
					return;
				}

				setApplePayAvailable(true);
				applePayMounted.current = true;

				if (applePayContainerRef.current) {
					applePayContainerRef.current.innerHTML = '';
				}

				await applePayHandler.mountPaymentButton('#account-apple-pay-container');

				const authorizedIntent = await applePayHandler.handlePayment();
				if (!isActive) return;

				if (!authorizedIntent || authorizedIntent.status !== 'authorized') {
					Toast.error('Apple Pay authorization was not completed.');
					return;
				}

				setLoading(true);

				try {
					const appleResponse = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/payment-sources/payment-intent`, {
						email: userData.email,
						paymentIntentId: authorizedIntent.id,
						paymentMethodType: 'apple_pay'
					});

					setLoading(false);
					await handleSuccess('Payment method updated successfully!', appleResponse.data?.paymentSource?.id);
				} catch (error) {
					setLoading(false);
					Toast.error(error.response?.data?.error || error.message || 'Failed to update Apple Pay payment source');
				}
			} catch (error) {
				if (isActive) {
					setApplePayAvailable(false);
					Toast.error(error.response?.data?.error || error.message || 'Unable to initialize Apple Pay');
				}
			}
		};

		initApplePay();

		return () => {
			isActive = false;
			applePayMounted.current = false;
			if (applePayContainerRef.current) {
				applePayContainerRef.current.innerHTML = '';
			}
		};
	}, [open, selectedNewPaymentType]);

	if (!open) return null;

	const recentInvoices = Array.isArray(subscriptionDetails?.recentInvoices) ? subscriptionDetails.recentInvoices : [];
	const INVOICES_PREVIEW_COUNT = 3;
	const visibleInvoices = invoicesExpanded ? recentInvoices : recentInvoices.slice(0, INVOICES_PREVIEW_COUNT);

	return (
		<div className="checkout-modal-overlay">
			<div className="checkout-modal-container account-billing-modal">
				<div className="checkout-modal-header">
					<div className="checkout-modal-header__left">
						<button
							type="button"
							className="account-billing-modal__back"
							onClick={onClose}
							disabled={loading}
							aria-label="Back to Manage Subscription"
						>
							<ArrowBackIcon fontSize="small" />
							<span>Back</span>
						</button>
						<div className="account-billing-modal__header-copy">
							<h2>Billing Management</h2>
							<p className="account-billing-modal__subtitle">
								Manage your payment methods and view previous invoices.
							</p>
						</div>
					</div>
					<button className="checkout-close-btn" onClick={onClose} disabled={loading} aria-label="Close">
						&#x2715;
					</button>
				</div>

				<div className="checkout-body">
					<div className="account-billing-modal__layout">
						<section className="account-billing-modal__card account-billing-modal__current-methods">
							<div className="account-billing-modal__section-header">
								<div>
									<h4>Current Payment Methods</h4>
									<p>See which saved billing methods are currently available for this user.</p>
								</div>
							</div>

							{paymentMethodsLoading ? (
								<div className="account-billing-modal__empty-state">Loading current payment methods...</div>
							) : currentPaymentMethods.length > 0 ? (
								<div className="account-billing-modal__payment-method-list">
									{currentPaymentMethods.map((paymentMethod, index) => (
										<div
											key={paymentMethod.id || paymentMethod.reference_id || `${paymentMethod.normalizedType}-${index}`}
											className={`account-billing-modal__payment-method-row ${
												paymentMethod.isPrimary ? 'account-billing-modal__payment-method-row--primary' : ''
											}`}
										>
											<div className="account-billing-modal__payment-method-main">
												<div className="account-billing-modal__payment-method-title">
													<strong>{paymentMethod.displayLabel || formatPaymentMethodType(paymentMethod.normalizedType)}</strong>
												</div>
												<span>{paymentMethod.maskedDetails || 'Saved payment method on file'}</span>
											</div>
											<div className="account-billing-modal__payment-method-meta">
												<span>{formatPaymentMethodType(paymentMethod.normalizedType)}</span>
												{paymentMethod.expMonth && paymentMethod.expYear ? (
													<strong>
														Exp {String(paymentMethod.expMonth).padStart(2, '0')}/{paymentMethod.expYear}
													</strong>
												) : null}
												{paymentMethod.isPrimary ? (
													<span className="account-billing-modal__primary-badge">Primary</span>
												) : (
													<button
														type="button"
														className="account-billing-modal__primary-action"
														onClick={() => setPrimaryPaymentMethod(paymentMethod.id)}
														disabled={settingPrimary || !paymentMethod.id}
													>
														{settingPrimaryId === paymentMethod.id ? 'Saving...' : 'Set as Primary'}
													</button>
												)}
											</div>
										</div>
									))}
								</div>
							) : (
								<div className="account-billing-modal__empty-state">
									No current saved payment methods were found for this user.
								</div>
							)}
						</section>

						<div className="checkout-payment-panel account-billing-modal__card">
							<div className="checkout-payment-panel__header">
								<h4>Update Payment Method</h4>
								<p className="account-billing-modal__panel-copy">
									Choose how you want future subscription charges to be billed.
								</p>
							</div>

							<div className="checkout-method-selector">
								<button
									type="button"
									className={`checkout-method-chip ${selectedNewPaymentType === 'card' ? 'is-active' : ''}`}
									onClick={() => setSelectedNewPaymentType('card')}
									disabled={loading}
								>
									Card
								</button>
								<button
									type="button"
									className={`checkout-method-chip ${selectedNewPaymentType === 'paypal' ? 'is-active' : ''}`}
									onClick={() => setSelectedNewPaymentType('paypal')}
									disabled={loading}
								>
									PayPal
								</button>
								{/* <button
									type="button"
									className={`checkout-method-chip ${selectedNewPaymentType === 'apple_pay' ? 'is-active' : ''}`}
									onClick={() => setSelectedNewPaymentType('apple_pay')}
									disabled={loading}
								>
									Apple Pay
								</button> */}
							</div>

							{selectedNewPaymentType === 'card' && (
								<div className="checkout-form-grid">
									<div>
										<label className="checkout-label" htmlFor="accountCardholderName">Cardholder Name</label>
										<div className="checkout-input-with-icon">
											<PersonOutlineOutlinedIcon className="checkout-input-icon" />
											<input
												id="accountCardholderName"
												className="checkout-input checkout-input--with-icon"
												type="text"
												placeholder="Jane Doe"
												value={paymentData.cardholderName}
												onChange={e => handleInputChange('cardholderName', e.target.value)}
												disabled={loading}
											/>
										</div>
										{errors.cardholderName && <p className="checkout-error-text">{errors.cardholderName}</p>}
									</div>

									<div className="checkout-card-country-row">
										<div>
											<label className="checkout-label" htmlFor="accountCardNumber">Card Number</label>
											<div className="checkout-input-with-icon">
												<CreditCardOutlinedIcon className="checkout-input-icon" />
												<input
													id="accountCardNumber"
													className="checkout-input checkout-input--with-icon"
													type="text"
													placeholder="XXXX XXXX XXXX XXXX"
													value={paymentData.cardNumber}
													onChange={e => handleInputChange('cardNumber', e.target.value)}
													disabled={loading}
												/>
											</div>
											{errors.cardNumber && <p className="checkout-error-text">{errors.cardNumber}</p>}
										</div>

										<div>
											<label className="checkout-label" htmlFor="accountCountry">Country</label>
											<div className="checkout-input-with-icon">
												<select
													id="accountCountry"
													className="checkout-input checkout-input--with-icon"
													value={paymentData.country}
													onChange={e => handleInputChange('country', e.target.value)}
													disabled={loading}
												>
													{COUNTRIES.map(country => (
														<option key={country.code} value={country.code}>
															{country.name}
														</option>
													))}
												</select>
											</div>
											{errors.country && <p className="checkout-error-text">{errors.country}</p>}
										</div>
									</div>

									<div className="checkout-small-row">
										<div>
											<label className="checkout-label" htmlFor="accountCardExpiryDate">Expiry Date</label>
											<div className="checkout-input-with-icon">
												<CalendarMonthOutlinedIcon className="checkout-input-icon" />
												<input
													id="accountCardExpiryDate"
													className="checkout-input checkout-input--with-icon"
													type="text"
													placeholder="MM/YY"
													value={paymentData.cardExpiry}
													onChange={e => handleInputChange('cardExpiry', e.target.value)}
													disabled={loading}
												/>
											</div>
											{errors.cardExpiry && <p className="checkout-error-text">{errors.cardExpiry}</p>}
										</div>

										<div>
											<label className="checkout-label" htmlFor="accountCardCvv">CVV</label>
											<div className="checkout-input-with-icon">
												<AddCardOutlinedIcon className="checkout-input-icon" />
												<input
													id="accountCardCvv"
													className="checkout-input checkout-input--with-icon"
													type="text"
													placeholder="CVV"
													value={paymentData.cardCvv}
													onChange={e => handleInputChange('cardCvv', e.target.value)}
													disabled={loading}
												/>
											</div>
											{errors.cardCvv && <p className="checkout-error-text">{errors.cardCvv}</p>}
										</div>

										<div>
											<label className="checkout-label" htmlFor="accountZipCode">Zip/Postal Code</label>
											<div className="checkout-input-with-icon">
												<NearMeOutlinedIcon className="checkout-input-icon" />
												<input
													id="accountZipCode"
													className="checkout-input checkout-input--with-icon"
													type="text"
													placeholder="#####"
													value={paymentData.zipCode}
													onChange={e => handleInputChange('zipCode', e.target.value)}
													disabled={loading}
												/>
											</div>
											{errors.zipCode && <p className="checkout-error-text">{errors.zipCode}</p>}
										</div>
									</div>
								</div>
							)}

							{selectedNewPaymentType === 'paypal' && (
								<div className="checkout-wallet-section">
									<p>Authorize PayPal below to save it as your payment method.</p>
									{paypalLoading && <div className="checkout-wallet-loading">Loading PayPal...</div>}
									<div className="checkout-wallet-row">
										<div className="checkout-wallet-btn-wrapper">
											<div
												id="account-paypal-container"
												style={{ display: paypalLoading ? 'none' : 'block', width: '100%', height: '100%' }}
											/>
										</div>
									</div>
								</div>
							)}

							{selectedNewPaymentType === 'apple_pay' && (
								<div className="checkout-wallet-section">
									<p>Use Apple Pay below to save it as your payment method.</p>
									{!applePayAvailable && (
										<div className="checkout-wallet-loading">
											Apple Pay is only available on supported Apple devices and browsers.
										</div>
									)}
									<div className="checkout-wallet-row">
										<div
											id="account-apple-pay-container"
											ref={applePayContainerRef}
											className="checkout-wallet-btn checkout-wallet-btn--apple"
										/>
									</div>
								</div>
							)}

							<div className="checkout-action-row">
								<button
									type="button"
									className="checkout-submit"
									onClick={handleCardPaymentSourceUpdate}
									disabled={loading || selectedNewPaymentType !== 'card'}
								>
									{loading ? 'Processing...' : 'Save Payment Method'}
								</button>
								<button type="button" className="checkout-cancel-btn" onClick={onClose} disabled={loading}>
									Cancel
								</button>
							</div>
						</div>

						<section className="account-billing-modal__card account-billing-modal__invoices">
							<div className="account-billing-modal__section-header">
								<div>
									<h4>Previous Invoices</h4>
									<p>Review your recent billing history from Chargebee.</p>
								</div>
							</div>

							{recentInvoices.length > 0 ? (
								<>
									<div className="account-billing-modal__invoice-scroll">
										<div className="account-billing-modal__invoice-list">
											{visibleInvoices.map(invoice => (
												<div key={invoice.id} className="account-billing-modal__invoice-row">
													<div className="account-billing-modal__invoice-main">
														<strong>{invoice.id || 'Invoice'}</strong>
														<span>
															{formatUnixDate(invoice.date)} • {formatInvoiceStatus(invoice.status)}
														</span>
													</div>
													<div className="account-billing-modal__invoice-meta">
														<strong>{formatMinorUnitAmount(invoice.total, invoice.currencyCode)}</strong>
														<span>
															{invoice.amountDue > 0
																? `${formatMinorUnitAmount(invoice.amountDue, invoice.currencyCode)} due`
																: 'Paid or settled'}
														</span>
													</div>
													{invoice.invoicePdfUrl ? (
														<a
															href={invoice.invoicePdfUrl}
															target="_blank"
															rel="noreferrer"
															className="account-billing-modal__invoice-link"
														>
															View Invoice
														</a>
													) : (
														<span className="account-billing-modal__invoice-link account-billing-modal__invoice-link--disabled">
															No PDF
														</span>
													)}
												</div>
											))}
										</div>
									</div>
									{recentInvoices.length > INVOICES_PREVIEW_COUNT && (
										<button
											type="button"
											className="account-billing-modal__invoice-toggle"
											onClick={() => setInvoicesExpanded(prev => !prev)}
										>
											{invoicesExpanded ? 'Show less' : `Show all ${recentInvoices.length} invoices`}
										</button>
									)}
								</>
							) : (
								<div className="account-billing-modal__empty-state">
									No previous invoices were found for this subscription yet.
								</div>
							)}
						</section>
					</div>
				</div>
			</div>

			{primaryPrompt && (
				<div className="checkout-modal-overlay">
					<div className="checkout-modal-container" style={{ maxWidth: '400px' }}>
						<div className="checkout-modal-header">
							<div className="checkout-modal-header__left">
								<h2>Set as Primary?</h2>
							</div>
							<button
								className="checkout-close-btn"
								onClick={handleSkipPrimary}
								disabled={settingPrimary}
								aria-label="Close"
							>
								&#x2715;
							</button>
						</div>

						<div className="checkout-body">
							<p style={{ marginBottom: '24px', color: '#e2ecf5b3', fontSize: '14px' }}>
								Would you like to set this as your primary payment method for future charges?
							</p>

							<div className="checkout-action-row" style={{ gap: '12px' }}>
								<button
									type="button"
									className="checkout-submit"
									onClick={handleSetPrimary}
									disabled={settingPrimary}
								>
									{settingPrimary ? 'Setting...' : 'Yes'}
								</button>
								<button
									type="button"
									className="checkout-cancel-btn"
									onClick={handleSkipPrimary}
									disabled={settingPrimary}
								>
									No Thanks
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default MyAccountPaymentMethodModal;
